import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { requestTokenFromSiblings, broadcastLogin, broadcastLogout, startSessionSync } from "@/lib/sessionSync";
import { setVisibilityFromUser } from "@/lib/privacy";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserRaw] = useState(null);
  const setUser = (u) => setUserRaw((prev) => { const next = typeof u === "function" ? u(prev) : u; setVisibilityFromUser(next); return next; });
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    // Spec iter309 (sécurité) — le jeton vit en sessionStorage : il expire à
    // la fermeture complète du navigateur → OTP redemandé. On purge tout
    // ancien jeton persistant (localStorage) hérité des versions précédentes.
    try { localStorage.removeItem("ledesk_token"); } catch { /* ignore */ }
    startSessionSync();
    // Spec iter310 — onglet neuf : récupère le jeton d'un autre onglet ouvert
    // (partage inter-onglets ; l'OTP n'est redemandé qu'après fermeture totale).
    const token = sessionStorage.getItem("ledesk_token") || (await requestTokenFromSiblings());
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      sessionStorage.removeItem("ledesk_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
    const onChanged = () => fetchMe();  // login/logout diffusé par un autre onglet
    window.addEventListener("ledesk-auth-changed", onChanged);
    return () => window.removeEventListener("ledesk-auth-changed", onChanged);
  }, [fetchMe]);

  // LOT 4 — recalcul des droits effectifs sans reconnexion : retour de focus,
  // événement `rights:changed` (émis après toute modification de profil/droits) et filet 60 s.
  const refreshRights = useCallback(async () => {
    if (!sessionStorage.getItem("ledesk_token")) return;
    try {
      const { data } = await api.get("/auth/me");
      setUser((prev) => (prev && JSON.stringify(prev.rights) === JSON.stringify(data.rights) && prev.profile_id === data.profile_id ? prev : data));
    } catch (e) {
      if (e?.response?.status === 401) { sessionStorage.removeItem("ledesk_token"); setUser(null); }
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const onFocus = () => refreshRights();
    const onVisible = () => { if (document.visibilityState === "visible") refreshRights(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("rights:changed", onFocus);
    const id = setInterval(refreshRights, 60000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("rights:changed", onFocus);
      clearInterval(id);
    };
  }, [user?.id, refreshRights]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = (token, u) => {
    sessionStorage.setItem("ledesk_token", token);
    broadcastLogin(token);
    setUser(u);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    sessionStorage.removeItem("ledesk_token");
    broadcastLogout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
